// "Call <name>" (actions contract row 3; REQ-2026-09-24-07): the page's side of a call. Pressing Call asks the API to
// place it (POST /api/triage/items/:passItemId/call: a Teams meeting on his calendar, recording on, the other person
// asked to join) and opens the meeting's link, so Teams opens with the call ("The Teams meeting opens for you
// straight away", the option Mike chose on 2026-09-24). The card stays, drawn anew: what becomes of it is decided
// from the call (Mike: "It listens to what was said on the call and makes a decision on what to do after that").
//
// Here and not in `src/api.ts`, as the reply flow's calls are (reply/api.ts), so no other track's file is edited.
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated, type PassItem, type Store } from '../api.js';

export interface CallResult {
  store: Store;
  passItemId: string;
  /** The Teams meeting's join link, from Graph. */
  joinUrl: string;
  call: { callee: { name: string | null; internal: boolean }; prompt: 'ring' | 'chat' | 'invite' | 'none'; recording: 'teams' | 'bot' | 'not_switched'; startsAt: string; video?: boolean };
  /** Set when the call went and something about it did not: the server's own words. */
  warning: string | null;
  decision: { id: string; action: 'accept' } | null;
  /** The item as it stands, or null when it could not be drawn again. */
  item: PassItem | null;
}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json()
      .then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null))
      .catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

/**
 * "Video call" beside every Call (REQ-2026-09-24-11; Mike, by pop-up: "Video call says so"): the same recorded meeting,
 * its words to the other person and on the calendar saying it is a video call. Cobrain cannot switch a camera on for a
 * Teams meeting, so he switches his on in Teams. The button's words are his option's own.
 */
export const VIDEO_CALL = 'Video call';

export function placeCall(passItemId: string, body: { store: string; optionKey: string; video?: boolean; explanationViewed?: boolean }): Promise<CallResult> {
  return fetch(`/api/triage/items/${encodeURIComponent(passItemId)}/call`, {
    method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  }).then((r) => json<CallResult>(r));
}

/** Only a Teams meeting link is opened: the page never navigates to an address it did not expect. */
export function isTeamsJoinLink(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && (u.hostname === 'teams.microsoft.com' || u.hostname.endsWith('.teams.microsoft.com'));
  } catch {
    return false;
  }
}

/** Open the meeting in Teams itself, preserving its escaped path and query without re-encoding them. */
export function teamsAppLink(url: string): string | null {
  if (!isTeamsJoinLink(url)) return null;
  // Read a normal HTTPS link verbatim: URL.pathname would resolve dot segments and URL.search drops a bare '?'.
  const parts = /^https:\/\/[^/?#]+(\/[^?#]*)?(\?[^#]*)?/i.exec(url);
  if (parts) return `msteams:${parts[1] ?? '/'}${parts[2] ?? ''}`;
  const u = new URL(url);
  return `msteams:${u.pathname}${u.search}`;
}

/**
 * Reserve a tab during the press, then close it when the meeting arrives. The current window hands the msteams:
 * link to the operating system without loading Teams' HTTPS launcher. The desktop app's will-navigate handler
 * hands this non-portal address to shell.openExternal, keeping the portal in its window too.
 */
export function openCallWindow(win: Pick<Window, 'open' | 'location'> = window): { go(url: string): boolean; close(): void } {
  let w: Window | null = null;
  try { w = win.open('', '_blank'); } catch { w = null; }
  return {
    go(url: string): boolean {
      const link = teamsAppLink(url);
      try { w?.close(); } catch { /* already gone */ }
      if (!link) return false;
      win.location.assign(link);
      return true;
    },
    close(): void { try { w?.close(); } catch { /* already gone */ } },
  };
}

/** The line under the card after a call: what was chosen, how the other person was asked, and anything that did not go. */
export function callSaid(label: string, r: Pick<CallResult, 'call' | 'warning'>): string {
  const who = r.call.callee.name ? `${r.call.callee.name} has` : 'They have';
  const asked = r.call.prompt === 'ring' ? `Cobrain is ringing ${r.call.callee.name ?? 'them'} in Teams.` : r.call.prompt === 'chat' ? `${who} been sent a Teams message to join.` : r.call.prompt === 'invite' ? `${who} been sent an invite to join.` : '';
  return [`Chosen: ${label}.`, asked, r.warning ?? ''].filter((x) => x !== '').join(' ');
}

// ---- the Chats page's Audio call and Video call (BUGS.md M3; REQ-2026-10-05-09) -----------------------------------------------
//
// Mike, 5 Oct 2026: "when you select to call a person from chats, the objective is that it records and registers the
// call in meetings as cobrain". Every Audio call and Video call on the Chats page asks the API (POST /api/chats/teams/:id/call),
// which makes the recorded meeting and has Cobrain's calling bot ring the colleagues, as Call on a card does; the page
// opens the meeting for video, while audio rings him too without opening anything. There is no unrecorded call:
// Mike, 15 Oct 2026, "There is to be no call that is not recorded" (REQ-2026-10-15-41).
// When the bot cannot ring, the page keeps the failure words without a second fallback link.

/** MIRROR of the route's answer in apps/api/src/routes/chats.ts. */
export interface ChatCallResult {
  threadId: string;
  joinUrl: string;
  /** False when the bot could not ring: nobody was asked, and `plain` is the ordinary Teams call to offer. */
  rung: boolean;
  callerRung?: boolean;
  call: { callees: string[]; prompt: 'ring' | 'chat' | 'invite' | 'none'; recording: 'teams' | 'bot' | 'not_switched'; startsAt: string; video: boolean };
  warning: string | null;
  plain: string | null;
}

/** A press's outcome, a refusal included, with what the server said: its words, its code and the plain call it offers. */
export type ChatCallOutcome =
  | { ok: true; result: ChatCallResult }
  | { ok: false; status: number; error: string; code: string | null; plain: string | null };

/** The plain Teams call, and nothing else: the msteams: call link the server builds (chats/people.ts callLink). */
export function isTeamsCallLink(url: string): boolean {
  return url.startsWith('msteams:/l/call/0/0?users=');
}

/** One press of Audio call or Video call in a conversation: `to` rings one person, none rings everybody in it now. */
export async function placeChatCall(store: string | null, threadId: string, body: { to?: string; video?: boolean }, fetcher: typeof fetch = (i, o) => fetch(i, o)): Promise<ChatCallOutcome> {
  const res = await fetcher(`/api/chats/teams/${encodeURIComponent(threadId)}/call${store ? `?store=${encodeURIComponent(store)}` : ''}`, {
    method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  });
  if (res.status === 401) throw new Unauthenticated();
  const said = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.ok && said) return { ok: true, result: said as unknown as ChatCallResult };
  const text = (k: string): string | null => (typeof said?.[k] === 'string' ? said[k] as string : null);
  return { ok: false, status: res.status, error: text('error') ?? `${res.status} ${res.statusText}`, code: text('code'), plain: text('plain') };
}

const listed = (names: string[]): string => (names.length <= 1 ? (names[0] ?? 'them') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`);

/** What the page says after a recorded press. */
export function chatCallSaid(o: ChatCallOutcome): { said: string } {
  if (!o.ok) return { said: o.error };
  const r = o.result;
  if (!r.rung) return { said: r.warning ?? 'Cobrain\'s calling bot could not ring them, so nobody has been rung.' };
  // Mike, 15 Oct 2026, by pop-up (about 19:50 CEST): an Audio call's line names you and each colleague by first name,
  // "Cobrain is ringing you and Conor in Teams." An address Teams gave without a name is shown whole.
  const rung = r.callerRung ? ['you', ...r.call.callees.map((n) => (n.includes('@') ? n : (n.trim().split(/\s+/)[0] ?? n)))] : r.call.callees;
  return { said: [`Cobrain is ringing ${listed(rung)} in Teams.`, r.warning ?? ''].filter((x) => x !== '').join(' ') };
}
